import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg, reportClientError } from "@/lib/api";
import { uploadFileSmart, uploadLimitError } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { PageHeader } from "@/components/shared";
import { PAGE_HELP } from "@/components/help";
import { FileText, LifeBuoy, Paperclip, Send, Trash2, Upload, X } from "lucide-react";

const CATEGORIES = [
  { value: "bug", label: "Bug / problem report" },
  { value: "pet_media", label: "Pet pictures / notes / videos" },
  { value: "billing", label: "Billing & subscription" },
  { value: "privacy", label: "Privacy & data" },
  { value: "general", label: "General question" },
];

const STATUS_STYLE = {
  open: "bg-[hsl(var(--info)/0.12)] text-[hsl(var(--info))]",
  in_progress: "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]",
  resolved: "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]",
};

const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`);

export const AttachmentChip = ({ att, onOpen }) => (
  <button
    onClick={() => onOpen(att)}
    className="inline-flex items-center gap-1.5 text-xs bg-secondary hover:bg-secondary/70 transition-colors rounded-lg px-2.5 py-1.5 max-w-[220px]"
    data-testid={`attachment-chip-${att.id}`}
  >
    <Paperclip className="w-3 h-3 shrink-0" />
    <span className="truncate">{att.filename}</span>
  </button>
);

export default function Support() {
  const [tickets, setTickets] = useState(null);
  const [form, setForm] = useState({ subject: "", category: "bug", description: "" });
  const [files, setFiles] = useState([]); // {file, id?, uploading}
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/support-tickets?limit=100");
      setTickets(data);
    } catch (e) { toast.error(errMsg(e)); setTickets([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickFiles = (e) => {
    const chosen = Array.from(e.target.files || []).slice(0, 10 - files.length);
    // Enforce limits up front (photos are auto-compressed; videos up to 200 MB via chunked upload)
    const ok = [];
    for (const file of chosen) {
      const err = uploadLimitError(file);
      if (err) toast.error(err);
      else ok.push(file);
    }
    // Stable uid per pending file so React keys survive removals/reorders
    setFiles((f) => [...f, ...ok.map((file) => ({ file, id: null, uid: crypto.randomUUID() }))]);
    e.target.value = "";
  };

  const removeFile = (uid) => setFiles((f) => f.filter((item) => item.uid !== uid));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) { toast.error("Subject and description are required."); return; }
    setBusy(true);
    try {
      // 1) upload attachments (free on all plans) - photos auto-compressed, big videos chunked
      const attachmentIds = [];
      for (const item of files) {
        try {
          if (item.file.size > 24 * 1024 * 1024) toast.info(`Uploading "${item.file.name}" — large videos take a little longer...`);
          const up = await uploadFileSmart(item.file, { silent: true });
          attachmentIds.push(up.id);
        } catch (upErr) {
          reportClientError("support.attachment_upload", upErr, { filename: item.file.name, size: item.file.size, type: item.file.type });
          toast.error(errMsg(upErr, `"${item.file.name}" couldn't be uploaded. Remove it and try again, or submit without attachments.`));
          setBusy(false);
          return;
        }
      }
      // 2) create the ticket
      await api.post("/voice/support-ticket", { ...form, attachmentIds });
      toast.success("Ticket submitted — our team will follow up. Thank you for helping us improve!");
      setForm({ subject: "", category: "bug", description: "" });
      setFiles([]);
      load();
    } catch (e2) {
      reportClientError("support.ticket_submit", e2, { subject: form.subject?.slice(0, 80) });
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  const openAttachment = async (att) => {
    try {
      const { data } = await api.get(`/uploads/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader title="Support & Feedback" help={PAGE_HELP.support} subtitle="Report problems, share edge cases, and attach pictures, notes, or videos about your pets — free on every plan." />

      <Card className="rounded-2xl mb-8" data-testid="support-form-card">
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="subject">Subject</Label>
                <Input id="subject" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Short summary of the problem or feedback" maxLength={200} required data-testid="support-subject-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="support-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="description">What happened?</Label>
              <Textarea id="description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Steps to reproduce, what you expected, what you saw instead... The more detail the better." rows={5} maxLength={4000} required data-testid="support-description-input" />
            </div>

            <div className="space-y-2">
              <Label>Attachments <span className="text-muted-foreground font-normal">(pictures, notes, videos — max 10 files; photos auto-compress, videos up to 200 MB)</span></Label>
              <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,audio/*,text/*,.pdf" className="hidden" onChange={pickFiles} data-testid="support-file-input" />
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => fileInputRef.current?.click()} disabled={files.length >= 10} data-testid="support-add-files-button">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Add files
                </Button>
                {files.map((item) => (
                  <span key={item.uid} className="inline-flex items-center gap-1.5 text-xs bg-secondary rounded-lg px-2.5 py-1.5 max-w-[240px]" data-testid={`support-pending-file-${item.uid}`}>
                    <Paperclip className="w-3 h-3 shrink-0" />
                    <span className="truncate">{item.file.name}</span>
                    <span className="text-muted-foreground shrink-0">{fmtSize(item.file.size)}</span>
                    <button type="button" onClick={() => removeFile(item.uid)} className="hover:text-[hsl(var(--destructive))]" aria-label="Remove file" data-testid={`support-remove-file-${item.uid}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>

            <Button type="submit" className="rounded-xl" disabled={busy} data-testid="support-submit-button">
              <Send className="w-4 h-4 mr-2" /> {busy ? "Submitting..." : "Submit ticket"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <h2 className="text-sm font-semibold mb-3 flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-primary" /> My tickets</h2>
      {tickets === null ? (
        <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : tickets.length === 0 ? (
        <Card className="rounded-2xl" data-testid="support-empty-state">
          <CardContent className="p-8 text-center">
            <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No tickets yet. Found something odd? Report it above — beta feedback makes Soula Paws better for everyone.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="support-ticket-list">
          {tickets.map((t) => (
            <Card key={t.id} className="rounded-2xl" data-testid={`support-ticket-${t.id}`}>
              <CardContent className="p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold flex-1">{t.subject}</p>
                  <Badge variant="secondary" className="border-0 text-[10px] capitalize">{(t.category || "general").replace("_", " ")}</Badge>
                  <Badge variant="secondary" className={`border-0 text-[10px] capitalize ${STATUS_STYLE[t.status] || ""}`} data-testid={`support-ticket-status-${t.id}`}>{(t.status || "open").replace("_", " ")}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1.5 whitespace-pre-wrap">{t.description}</p>
                {(t.replies || []).length > 0 && (
                  <div className="mt-3 space-y-2" data-testid={`support-ticket-replies-${t.id}`}>
                    {t.replies.map((r, i) => (
                      <div key={`${r.at || "reply"}-${i}`} className="rounded-xl bg-[hsl(var(--primary)/0.06)] border border-[hsl(var(--primary)/0.2)] p-3">
                        <p className="text-xs font-semibold text-primary">{r.byName || "Soula Paws Team"} replied</p>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{r.message}</p>
                        <p className="text-[10px] text-muted-foreground mt-1.5">{new Date(r.at).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                )}
                {(t.attachmentIds || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {t.attachmentIds.map((id) => <AttachmentChip key={id} att={{ id, filename: `Attachment ${id.slice(0, 8)}` }} onOpen={openAttachment} />)}
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground mt-3">Created {new Date(t.createdAt).toLocaleString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

